import type {BdSharePermission} from './shared.model';

/**
 * This class offers functionality to handle permission-related checks.
 */
export class BdSharePermissionHelper {

    /**
     * Get all permissions below the given permission.
     *
     * Examples:
     * BdSharePermissionHelper.includedLower('DELETE') => ['DELETE', 'UPDATE', 'READ']
     * BdSharePermissionHelper.includedLower('UPDATE') => ['UPDATE', 'READ']
     *
     * @param permission
     */
    public static includedLower(permission: BdSharePermission): BdSharePermission[] {
        const possible: BdSharePermission[] = ['DELETE', 'UPDATE', 'READ'];
        const permissionIndex = possible.indexOf(permission);

        if (permissionIndex === -1) {
            throw new Error(`Invalid permission type: ${permission}`);
        } else {
            return possible.slice(permissionIndex);
        }
    }

    /**
     * Get all permissions between the given permission.
     *
     * Examples:
     * BdSharePermissionHelper.includedBetween('READ', 'DELETE') ==> ['READ', 'UPDATE', 'DELETE']
     * BdSharePermissionHelper.includedBetween('UPDATE') ==> ['UPDATE', 'DELETE']
     * BdSharePermissionHelper.includedBetween() ==> ['READ', 'UPDATE', 'DELETE']
     *
     * @param minPermission
     * @param maxPermission
     */
    public static includedBetween(
        minPermission: BdSharePermission = 'READ',
        maxPermission: BdSharePermission = 'DELETE'
    ): BdSharePermission[] {
        const possible: BdSharePermission[] = ['READ', 'UPDATE', 'DELETE'];

        const indexFnc = (permission: BdSharePermission): number | null => {
            const index = possible.indexOf(permission);

            return index > -1 ? index : null;
        };

        const permissionMinIndex = indexFnc(minPermission) || 0;

        // + 1 to include maxPermission in a result, slice end is non-inclusive
        const permissionMaxIndex = indexFnc(maxPermission) + 1 || 3;

        return possible.slice(permissionMinIndex, permissionMaxIndex);
    }

    /**
     * Check whether the `includesPermission` is hierarchically lower than the `givenPermission` and therefore included.
     *
     * Example
     * BdSharePermissionHelper.includes('DELETE', 'UPDATE') ==> true
     * BdSharePermissionHelper.includes('READ', 'DELETE') ==> false
     *
     * @param givenPermission
     * @param includesPermission
     */
    public static includes(
        givenPermission: BdSharePermission,
        includesPermission: BdSharePermission
    ): boolean {
        const includedPermissions = BdSharePermissionHelper.includedLower(givenPermission);

        return includedPermissions.indexOf(includesPermission) > -1;
    }

    /**
     * Get the highest permission from a given array of permissions.
     * The hierarchy is: 'DELETE' (highest) > 'UPDATE' > 'READ' (lowest)
     *
     * Examples:
     * BdSharePermissionHelper.getHighestPermission(['READ', 'UPDATE', 'DELETE']) => 'DELETE'
     * BdSharePermissionHelper.getHighestPermission(['READ', 'UPDATE']) => 'UPDATE'
     * BdSharePermissionHelper.getHighestPermission(['UPDATE']) => 'UPDATE'
     *
     * @param permissions Array of permissions to evaluate
     * @returns The highest permission from the array
     * @throws Error if the array is empty or contains invalid permissions
     */
    public static getHighestPermission(permissions: BdSharePermission[]): BdSharePermission {
        if (!permissions || permissions.length === 0) {
            throw new Error('Permissions array cannot be empty');
        }

        const permissionHierarchy: BdSharePermission[] = ['READ', 'UPDATE', 'DELETE'];

        // Validate all permissions are valid
        for (const permission of permissions) {
            if (!permissionHierarchy.includes(permission)) {
                throw new Error(`Invalid permission type: ${permission}`);
            }
        }

        // Find the highest permission by checking from highest to lowest
        for (let i = permissionHierarchy.length - 1; i >= 0; i--) {
            if (permissions.includes(permissionHierarchy[i])) {
                return permissionHierarchy[i];
            }
        }

        // This should never be reached due to validation above, but TypeScript requires it
        throw new Error('No valid permissions found');
    }
}
